import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Download,
} from '@phosphor-icons/react';
import { CERT_SCENARIOS } from './sandata-cert/constants';
import { useSandataCert } from './sandata-cert/useSandataCert';
import OverviewCard from './sandata-cert/OverviewCard';
import ScenarioRow from './sandata-cert/ScenarioRow';
import DetailPanel from './sandata-cert/DetailPanel';
import ApiStatusCard from './sandata-cert/ApiStatusCard';
import { MockedEvvBanner } from '../components/SandataModeBadge';

const TABS = [
  { value: 'all', label: 'All', count: CERT_SCENARIOS.length },
  { value: 'recipient', label: 'Recipients', count: 4 },
  { value: 'staff', label: 'Staff', count: 3 },
  { value: 'visit', label: 'Visits', count: 8 },
];

const SummaryBar = ({ successCount, failedCount, allPassed }) => (
  <div
    className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${
      allPassed ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
    }`}
  >
    <div className="flex items-center gap-3">
      {allPassed ? (
        <CheckCircle size={20} weight="fill" className="text-emerald-600" />
      ) : (
        <Clock size={20} weight="duotone" className="text-slate-500" />
      )}
      <span className="text-sm font-medium text-slate-800">
        {allPassed
          ? 'All scenarios validated — certification ready'
          : `${successCount} of ${CERT_SCENARIOS.length} scenarios passing`}
      </span>
    </div>
    <div className="flex gap-2 text-xs">
      {successCount > 0 && (
        <span className="text-emerald-700 font-medium">{successCount} passed</span>
      )}
      {failedCount > 0 && (
        <span className="text-red-600 font-medium">{failedCount} failed</span>
      )}
    </div>
  </div>
);

export default function SandataCertPage() {
  const {
    apiStatus,
    results,
    statuses,
    selectedScenario,
    setSelectedScenario,
    runAllActive,
    runAllProgress,
    processingStatuses,
    runScenario,
    checkProcessingStatus,
    runAll,
    stopAll,
    downloadJson,
  } = useSandataCert();

  const successCount = Object.values(statuses).filter((s) => s === 'success').length;
  const failedCount = Object.values(statuses).filter((s) => s === 'failed').length;
  const totalRun = Object.values(statuses).filter((s) => s !== 'idle').length;
  const allPassed = successCount === CERT_SCENARIOS.length;

  return (
    <div data-testid="sandata-cert-page" className="space-y-6">
      <MockedEvvBanner context="sandata-cert" />
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Sandata EVV Certification
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ohio Alt-EVV certification test suite — {CERT_SCENARIOS.length} scenarios
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="download-json-btn"
            variant="outline"
            size="sm"
            onClick={downloadJson}
            disabled={runAllActive}
            className="gap-1.5"
          >
            <Download size={14} /> Export JSON
          </Button>
          {runAllActive ? (
            <Button
              data-testid="stop-all-btn"
              variant="destructive"
              size="sm"
              onClick={stopAll}
              className="gap-1.5"
            >
              <XCircle size={14} /> Stop
            </Button>
          ) : (
            <Button
              data-testid="run-all-btn"
              size="sm"
              onClick={runAll}
              className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
            >
              <Play size={14} weight="fill" /> Run All Tests
            </Button>
          )}
        </div>
      </div>

      {/* Run All Progress */}
      {runAllProgress && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-amber-800">
                Running: {runAllProgress.label} ({runAllProgress.current}/{runAllProgress.total})
              </span>
              <span className="text-xs text-amber-600">
                {Math.round((runAllProgress.current / runAllProgress.total) * 100)}%
              </span>
            </div>
            <Progress
              value={(runAllProgress.current / runAllProgress.total) * 100}
              className="h-1.5"
            />
          </CardContent>
        </Card>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {['recipient', 'staff', 'visit'].map((group) => (
          <OverviewCard
            key={group}
            group={group}
            scenarios={CERT_SCENARIOS}
            statuses={statuses}
          />
        ))}
      </div>

      {totalRun > 0 && (
        <SummaryBar
          successCount={successCount}
          failedCount={failedCount}
          allPassed={allPassed}
        />
      )}

      {/* Main Grid: Scenario List + Detail Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Scenario List */}
        <div className="lg:col-span-3">
          <Card>
            <Tabs defaultValue="all">
              <div className="px-4 pt-3 pb-0">
                <TabsList className="h-8">
                  {TABS.map((t) => (
                    <TabsTrigger key={t.value} value={t.value} className="text-xs h-7">
                      {t.label} ({t.count})
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <Separator className="mt-3" />
              {TABS.map((tab) => (
                <TabsContent key={tab.value} value={tab.value} className="mt-0">
                  <ScrollArea className="max-h-[520px]">
                    {CERT_SCENARIOS.filter(
                      (s) => tab.value === 'all' || s.group === tab.value
                    ).map((s) => (
                      <ScenarioRow
                        key={s.id}
                        scenario={s}
                        result={results[s.id]}
                        status={statuses[s.id] || 'idle'}
                        onRun={runScenario}
                        onCheckStatus={checkProcessingStatus}
                        onSelect={setSelectedScenario}
                        isSelected={selectedScenario === s.id}
                      />
                    ))}
                  </ScrollArea>
                </TabsContent>
              ))}
            </Tabs>
          </Card>
        </div>

        {/* Detail Panel + API Status */}
        <div className="lg:col-span-2">
          <Card className="sticky top-4">
            <CardHeader className="pb-0 pt-3 px-4">
              <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Details
              </CardTitle>
            </CardHeader>
            <Separator className="mt-2" />
            <DetailPanel
              scenario={selectedScenario}
              result={results[selectedScenario]}
              processingStatus={processingStatuses[selectedScenario]}
            />
          </Card>

          <ApiStatusCard apiStatus={apiStatus} />
        </div>
      </div>
    </div>
  );
}
